import { useTranslation } from 'next-i18next'

import { useAppDispatch } from '~/redux/hooks'

import Button from '~/components/Buttons/Button'
import BootcampModules from '~/MathTopics/components/ProductCards/BootcampModules'
import MathVaultModules from '~/MathTopics/components/ProductCards/MathVaultModules'
import MathEssentialModules from '~/MathTopics/components/ProductCards/MathEssentialModules'
import EncyclopediaModules from '~/MathTopics/components/ProductCards/EncyclopediaModules'
import GameProductCard from '~/MathTopics/components/ProductCards/GameModules'
import { LearningProduct } from '~/MathTopics/types/math-topic.type'
import { getGameUrl } from '~/MathTopics/utils/math-topic.utils'

import mathTopicAnalytics from '~/analytics/mathTopics/mathTopicAnalytics'

import { setCurrentLocationCreator } from '~/redux/slices/math-vault'
import { VaultSpawnLocations } from '~/types/math-vault'
import { Link } from '~/components/Link'

interface ProductCardProps {
  topicId: string
  bgColor: string
  productId: string
  products: LearningProduct[]
}

export default function ProductCard(props: ProductCardProps): React.ReactElement {
  const { t } = useTranslation('mathTopics')

  const { topicId, bgColor, productId, products } = props

  const dispatch = useAppDispatch()

  const handleGoButtonClick = (item: LearningProduct): void => {
    mathTopicAnalytics.clickTopicProduct({
      topic: topicId,
      category: productId,
      title: item?.title || '',
    })

    if (productId === 'mathVault') {
      dispatch(setCurrentLocationCreator({ room: item.path as VaultSpawnLocations, chest: null }))
    }
  }

  const getProductLink = (item: LearningProduct): string => {
    return productId === 'games'
      ? getGameUrl(item.type || '')
      : productId === 'mathVault'
      ? '/math-vault'
      : item.path || ''
  }

  return (
    <div className="flex flex-wrap -mx-1 -my-1 md:-mx-2.5 md:-my-2.5 items-center">
      {products.map((item) => (
        <div
          className="w-1/2 md:w-1/4 h-auto px-1 py-1 md:px-2.5 md:py-2.5"
          key={`product-card-${item.title.toLowerCase()}`}
        >
          <div className="h-auto pb-4 items-center gap-2 rounded-[21.2px] bg-white shadow-low-opacity-black">
            <Link onClick={() => handleGoButtonClick(item)} to={getProductLink(item)}>
              <div
                className={`flex items-center justify-center ${bgColor} text-center text-white text-shadow-gray-light h-20 px-2.5 py-2.5 gap-2.5 shrink-0 self-stretch rounded-t-[21.2px] tracking-[0.378px] font-medium text-base leading-5`}
              >
                {item.title}
              </div>

              {productId === 'bootcamp' && <BootcampModules item={item} />}
              {productId === 'mathVault' && <MathVaultModules item={item} />}
              {productId === 'mathEssential' && <MathEssentialModules item={item} />}
              {productId === 'encyclopedia' && <EncyclopediaModules item={item} />}
              {productId === 'games' && <GameProductCard item={item} />}

              <Button
                className={`max-w-full mx-auto h-6 ${bgColor} hover:bg-purple-dark hover:text-white px-4 rounded-5 text-center text-white text-base font-semibold`}
                noPadding
                size="medium"
              >
                {t('go')}
              </Button>
            </Link>
          </div>
        </div>
      ))}
    </div>
  )
}
